import React, { useEffect, useId, useRef, useState } from 'react';
import { acquireScrollLock, releaseScrollLock } from '../utils/scrollLock';
import useIsTestVersion from '../hooks/useIsTestVersion';
import usePopupUrlSync from '../hooks/usePopupUrlSync';
import useSheetDrag from '../hooks/useSheetDrag';
import SheetHandle from './common/SheetHandle';

function Modal({ isOpen, onClose, title, description = null, children, closeOnBackdropClick = false, size = 'md', minHeight = '', zIndexClass = 'z-[60]', titleStyle = {}, closeButtonStyle = {}, contentPadding = 'p-4', contentClassName = '', headerClassName = '', className = '', ariaLabel = null, insetMobile = false, name = null }) {
  // Initialize mounting state based on the initial isOpen value. This prevents an initial
  // render that returns `null`, which in StrictMode can cause a brief flicker (content → white → content).
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(isOpen);
  const lockHeldRef = useRef(false);
  // Cancel pending timers when `isOpen` flips so a stale `setIsMounted(false)`
  // can't hide the modal while the scroll lock is still held.
  const animateTimerRef = useRef(null);
  const unmountTimerRef = useRef(null);
  // A11y: dialog container ref + the element focused before opening, so focus
  // can move into the dialog on open and return to the trigger on close.
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  // New look ("test" version of the redesign experiment) restyles the shell:
  // dark dimmed backdrop, rounded-2xl panel, left-aligned Idler header with an
  // optional one-line description. Control renders the historical styles.
  const isTest = useIsTestVersion();
  // New-look full-bleed bottom sheet on mobile; centered card from sm+.
  // Pass insetMobile to opt out (legacy inset card).
  const useMobileSheet = isTest && !insetMobile;
  // Sheet grows to a true full-screen overlay once the body is scrolled —
  // same gesture as the box selection tray / ProductModal.
  const [isExpanded, setIsExpanded] = useState(false);
  const { onDragStart, onDragMove, onDragEnd, resetDrag, dragStyle } = useSheetDrag(onClose);

  // Reflect the open popup in the URL (?modal=<name>) so third-party analytics
  // tools that key off URL/history changes can tell one popup flow from another.
  usePopupUrlSync(name, isOpen);

  // Close on Escape (WCAG 2.1.2 — keyboard users need a way out).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open; restore it to the trigger on close.
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      // Wait a tick so the dialog is mounted and visible before focusing.
      const t = setTimeout(() => dialogRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (animateTimerRef.current) {
      clearTimeout(animateTimerRef.current);
      animateTimerRef.current = null;
    }
    if (unmountTimerRef.current) {
      clearTimeout(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }

    if (isOpen) {
      setIsExpanded(false);
      resetDrag();
      if (!isMounted) setIsMounted(true);
      animateTimerRef.current = setTimeout(() => {
        setIsAnimating(true);
        animateTimerRef.current = null;
      }, 10);

      if (!lockHeldRef.current) {
        acquireScrollLock();
        lockHeldRef.current = true;
      }
    } else {
      setIsAnimating(false);
      resetDrag();
      unmountTimerRef.current = setTimeout(() => {
        setIsMounted(false);
        unmountTimerRef.current = null;
      }, useMobileSheet ? 350 : 500);

      if (lockHeldRef.current) {
        releaseScrollLock();
        lockHeldRef.current = false;
      }
    }

    return () => {
      if (animateTimerRef.current) {
        clearTimeout(animateTimerRef.current);
        animateTimerRef.current = null;
      }
      if (unmountTimerRef.current) {
        clearTimeout(unmountTimerRef.current);
        unmountTimerRef.current = null;
      }
      if (lockHeldRef.current) {
        releaseScrollLock();
        lockHeldRef.current = false;
      }
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture-phase scroll on the dialog: the sheet itself doesn't scroll, a
  // child (the body, or a nested overflow list) does. Expand once that
  // scroller leaves the top. Phone-only — from sm up this is a centered card.
  useEffect(() => {
    if (!isOpen || !useMobileSheet || !isMounted) return undefined;
    const root = dialogRef.current;
    if (!root) return undefined;
    const onScroll = (e) => {
      if (window.innerWidth >= 640) {
        setIsExpanded(false);
        return;
      }
      const target = e.target;
      if (!(target instanceof Element) || target === root) return;
      if (target.scrollHeight <= target.clientHeight + 1) return;
      const { scrollTop } = target;
      setIsExpanded((prev) => (prev ? scrollTop > 4 : scrollTop > 24));
    };
    root.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => root.removeEventListener('scroll', onScroll, { capture: true });
  }, [isOpen, useMobileSheet, isMounted]);

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isMounted) return null;

  // `wide` only affects sm+ max-width.
  const sizeClasses = size === 'wide'
    ? 'max-w-none sm:max-w-[480px] md:max-w-[540px]'
    : useMobileSheet
    ? (size === 'xl'
      ? 'max-w-none sm:max-w-[700px] md:max-w-[900px] lg:max-w-[1000px] xl:max-w-[1200px]'
      : size === 'lg'
      ? 'max-w-none sm:max-w-[500px] md:max-w-[600px] lg:max-w-[700px] xl:max-w-[900px]'
      : 'max-w-none sm:max-w-[420px] md:max-w-[500px] lg:max-w-[600px]')
    : size === 'xl'
    ? 'max-w-[calc(100vw-24px)] xs:max-w-[400px] sm:max-w-[700px] md:max-w-[900px] lg:max-w-[1000px] xl:max-w-[1200px]'
    : size === 'lg'
    ? 'max-w-[calc(100vw-24px)] xs:max-w-[380px] sm:max-w-[500px] md:max-w-[600px] lg:max-w-[700px] xl:max-w-[900px]'
    : 'max-w-[calc(100vw-24px)] xs:max-w-[350px] sm:max-w-[420px] md:max-w-[500px] lg:max-w-[600px]';

  const minHeightClass = minHeight;

  // Bottom-sheet chrome: custom className panels, or new-look default (unless insetMobile).
  const useSheetLayout = Boolean(className) || useMobileSheet;
  const backdropPadding = useSheetLayout
    ? 'p-0 sm:p-4'
    : 'p-3 xxs:p-3 xs:p-4 pt-4 xxs:pt-5 xs:pt-6 sm:pt-4';
  // Use items-start (not items-center) on the scrollable overlay. Flex
  // align-items:center offsets tall children into negative space, so the top
  // of the dialog (title / X) is clipped and can't be scrolled into view.
  // Short dialogs still center via my-auto on the panel.
  const backdropAlign = useSheetLayout
    ? 'flex items-end sm:items-start justify-center'
    : 'flex justify-center items-start';

  const backdropClasses = isTest
    ? `fixed inset-0 bg-black/40 ${zIndexClass} ${backdropAlign} ${backdropPadding} transition-opacity duration-300 motion-reduce:transition-none ${isAnimating ? 'opacity-100' : 'opacity-0'} overflow-y-auto`
    : `fixed inset-0 bg-[#fdf9f09e] bg-opacity-50 backdrop-blur-sm ${zIndexClass} ${className ? 'flex items-end lg:items-start justify-center' : 'flex justify-center items-start'} ${className ? 'p-0 lg:p-4' : 'p-3 xxs:p-3 xs:p-4 pt-4 xxs:pt-5 xs:pt-6 sm:pt-4'} transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'} overflow-y-auto`;

  const panelRadius = useMobileSheet
    ? 'rounded-t-2xl sm:rounded-2xl'
    : isTest
    ? 'rounded-2xl'
    : 'rounded-lg';
  // Anchor to the bottom on mobile sheet; center vertically from sm up.
  const panelMargin = useMobileSheet ? 'sm:my-auto' : 'my-auto';
  // Cap to the padded viewport so the dialog shell never sits off-screen;
  // body content scrolls inside (overflow-hidden + flex min-h-0).
  const panelMaxHeight = useMobileSheet
    ? 'max-h-[90dvh] sm:max-h-[calc(100dvh-2rem)]'
    : 'max-h-[calc(100dvh-2rem)]';

  const sheetMotion = useMobileSheet
    ? `transition-[transform,height,border-radius,padding] duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${
        isExpanded
          ? 'h-[100dvh] max-h-[100dvh] rounded-none pt-[env(safe-area-inset-top)] sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:pt-0'
          : `${panelMaxHeight} ${panelRadius}`
      } ${isAnimating ? 'translate-y-0 sm:opacity-100' : 'translate-y-full sm:translate-y-8 sm:opacity-0'}`
    : `transform transition-all duration-300 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`;

  const panelClasses = isTest
    ? `bg-white ${useMobileSheet ? '' : `${panelRadius} ${panelMaxHeight}`} w-full ${sizeClasses} ${minHeightClass} flex min-h-0 flex-col overflow-hidden ${panelMargin} shadow-[0_24px_60px_-16px_rgba(84,73,71,0.35)] relative ${sheetMotion}`
    : `bg-white rounded-lg w-full ${sizeClasses} ${minHeightClass} ${panelMaxHeight} flex min-h-0 flex-col overflow-hidden my-auto [box-shadow:rgb(113_45_33_/_40%)_0_-20px_100px_-20px] transform transition-all duration-500 ease-in-out ${isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} relative`;

  // New look: merge extra panel classes (e.g. ProductModal desktop sizing).
  // Old look with className still replaces the whole panel (historical).
  const panelClassName = (isTest || !className)
    ? `${panelClasses} ${className}`.trim()
    : className;

  const defaultHeaderClass = useMobileSheet
    ? 'flex items-start gap-2 px-4 pb-3 pt-2 sm:p-5 sm:pb-2 flex-shrink-0'
    : 'flex items-start gap-2 p-4 pb-2 sm:p-5 sm:pb-2 flex-shrink-0';

  const header = (
    <>
      {useMobileSheet && (
        <SheetHandle
          className="sm:hidden"
          onClose={onClose}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
        />
      )}
      {title && isTest && (
        <div className={headerClassName || defaultHeaderClass}>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 id={titleId} className="font-idler text-lg leading-snug text-[#544947]" style={titleStyle}>{title}</h3>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-[13px] font-normal normal-case tracking-normal text-gray-500 [text-wrap:pretty]">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`${useMobileSheet ? 'hidden sm:flex' : 'flex'} h-11 w-11 flex-none items-center justify-center rounded-full bg-transparent text-gray-400 transition-colors hover:bg-[#544947]/[0.06] hover:text-[#544947]`}
          >
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {title && !isTest && (
        <div className="flex items-center p-3 xxs:p-3.5 xs:p-4 border-b flex-shrink-0">
          <div className="flex-1"></div>
          <h3 id={titleId} className="text-base xxs:text-base xs:text-lg font-semibold flex-grow text-center transition-colors duration-300" style={titleStyle}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-300 hover:text-gray-700 ml-1 xxs:ml-1.5 xs:ml-2 flex-1 flex justify-end p-1.5 xxs:p-1.5 xs:p-2"
          >
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 xxs:h-5 xxs:w-5 xs:h-6 xs:w-6 transition-colors duration-300 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke={closeButtonStyle.stroke || "#FF6247"} style={closeButtonStyle}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {!title && isTest && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={`${useMobileSheet ? 'hidden sm:flex' : 'flex'} absolute top-2.5 right-2.5 z-30 h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#544947] shadow-md ring-1 ring-black/5 backdrop-blur transition-colors hover:bg-white hover:text-primary`}
        >
          <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {!title && !isTest && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 xxs:top-3.5 xxs:right-3.5 xs:top-4 xs:right-4 text-gray-300 hover:text-gray-700 z-10 p-1.5 xxs:p-1.5 xs:p-2"
        >
          <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 xxs:h-5 xxs:w-5 xs:h-6 xs:w-6 transition-colors duration-300 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke={closeButtonStyle.stroke || "#FF6247"} style={closeButtonStyle}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </>
  );

  const body = (
    <div className={`${contentPadding || 'p-3 xxs:p-3.5 xs:p-4'} ${isTest ? 'normal-case tracking-normal' : ''} ${contentClassName || 'min-h-0 overflow-y-auto flex-grow'}`}>
      {children}
    </div>
  );

  if (useMobileSheet) {
    return (
      <div className={`fixed inset-0 ${zIndexClass}`}>
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${
            isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeOnBackdropClick ? onClose : undefined}
        />
        <div className="pointer-events-none flex h-full items-end justify-center sm:items-start sm:overflow-y-auto sm:p-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={isTest && title && description ? descriptionId : undefined}
            aria-label={title ? undefined : (ariaLabel || 'Dialog')}
            className={`${panelClassName} pointer-events-auto w-full focus:outline-none`}
            style={dragStyle}
          >
            {header}
            {body}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={backdropClasses}
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={isTest && title && description ? descriptionId : undefined}
        aria-label={title ? undefined : (ariaLabel || 'Dialog')}
        className={`${panelClassName} focus:outline-none`}
      >
        {header}
        {body}
      </div>
    </div>
  );
}

export default Modal;
